import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

type ExecutionEngagementDetailsContextValue = {
  engagementType: string | null;
  isLimitedReviewExecution: boolean;
  loading: boolean;
  refreshExecutionEngagementDetails: () => Promise<void>;
};

const ExecutionEngagementDetailsContext = createContext<
  ExecutionEngagementDetailsContextValue | undefined
>(undefined);

export function ExecutionEngagementDetailsProvider({
  children,
  engagementId,
  activeSection,
}: {
  children: ReactNode;
  engagementId: string | undefined;
  activeSection: string;
}) {
  const [engagementType, setEngagementType] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchDetails = useCallback(async () => {
    const api =
      typeof window !== "undefined" ? (window as unknown as { sharePointAPI?: { getReportingDate?: () => Promise<unknown> } }).sharePointAPI : undefined;
    if (!api?.getReportingDate) return;
    setLoading(true);
    try {
      const res = (await api.getReportingDate()) as {
        success?: boolean;
        engagement_type?: string | null;
      };
      if (res?.success) {
        const et = res.engagement_type;
        setEngagementType(typeof et === "string" ? et : null);
      } else {
        setEngagementType(null);
      }
    } catch {
      setEngagementType(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!engagementId) {
      setEngagementType(null);
      return;
    }
    void fetchDetails();
  }, [engagementId, fetchDetails]);

  useEffect(() => {
    if (!engagementId) return;
    const shouldRefresh =
      activeSection === "execution" ||
      activeSection.startsWith("execution-") ||
      activeSection.startsWith("dynamic-workspace:Execution:");
    if (shouldRefresh) void fetchDetails();
  }, [activeSection, engagementId, fetchDetails]);

  const isLimitedReviewExecution = engagementType === "Limited Review";

  return (
    <ExecutionEngagementDetailsContext.Provider
      value={{
        engagementType,
        isLimitedReviewExecution,
        loading,
        refreshExecutionEngagementDetails: fetchDetails,
      }}
    >
      {children}
    </ExecutionEngagementDetailsContext.Provider>
  );
}

export function useExecutionEngagementDetails() {
  const ctx = useContext(ExecutionEngagementDetailsContext);
  if (!ctx) {
    throw new Error("useExecutionEngagementDetails must be used within ExecutionEngagementDetailsProvider");
  }
  return ctx;
}
